import React, { useState, Suspense } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  Text,
  Box,
  Spinner,
  Button,
  useToastContext,
} from '@resideo/blueprint-react';
import useCountryStateSelector from 'hooks/useCountryStateSelector';
import {
  InputField,
  PhoneInputField,
  CountrySelectorWrapper,
  StateSelectorWrapper,
  SelectField,
} from '@resideo/blueprint-formik';
import { Formik, Form, FormikErrors } from 'formik';
import { GOOGLE_MAPS_API_KEY } from 'config';
import { checkPhoneNumber } from 'utils/validation';
import { useRisClient } from 'hooks/useRisClient';
import { useMutation } from '@tanstack/react-query';
import { CountryCode } from '@resideo/web-integration-services-api-client';
import { getBusinessTypes } from 'utils/common';
import { useFeatureFlags } from 'context/FeatureFlags';
import { LoadScript } from '@react-google-maps/api';
import AutocompleteComponent from './Autocomplete';
import AddressVerificationModal from './AddressVerificationModal';
import { parseGeoAddress, GeocodingAddress } from 'utils/geocoding';

const libraries: 'places'[] = ['places'];

export interface CompanyInfoValues {
  name: string;
  countryCode: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipPostalCode: string;
  primaryPhoneNumber: string;
  typeOfBusiness: string;
}

const FormContainer = styled(Form)`
  width: 100%;
`;

const StyledPhoneInput = styled(PhoneInputField)`
  &.common-input-phone {
    border-color: #cccccc;
  }
  &.common-input-phone-error {
    border-color: red;
    border-width: 2px;
    color: black;
  }
`;

const NextButton = styled(Button)`
  width: 325px;

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    width: 262px;
  }
`;

interface CompanyInfoFormProps {
  email: string;
  setFormValues: (values: CompanyInfoValues) => void;
  setPartnerAccountMatch: (data: any[] | undefined) => void;
}

const initialValues: CompanyInfoValues = {
  name: '',
  countryCode: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipPostalCode: '',
  primaryPhoneNumber: '',
  typeOfBusiness: '',
};

const CompanyInfoForm = ({
  email,
  setFormValues,
  setPartnerAccountMatch,
}: CompanyInfoFormProps) => {
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [autoCompleteSelected, setAutoCompleteSelected] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] =
    useState<CompanyInfoValues | null>(null);
  const [suggestedAddress, setSuggestedAddress] =
    useState<GeocodingAddress | null>(null);
  const { client } = useRisClient();

  const findCompanyMatches = useMutation({
    mutationFn: async (vals: CompanyInfoValues) => {
      return await client.core.coreFindCompanyMatches({
        name: vals.name,
        addressLine1: vals.addressLine1,
        city: vals.city,
        stateProvinceRegionCode: vals.state,
        countryCode: vals.countryCode as CountryCode,
        primaryPhoneNumber: vals.primaryPhoneNumber,
        email: email,
      });
    },
    onError: () => {
      addToast({
        toastType: 'Error',
        message: t('common.toast.error'),
      });
    },
    onSuccess: ({ data }, variables) => {
      setFormValues(variables);
      setPartnerAccountMatch(data ?? undefined);
    },
  });

  const handleSubmit = async (values: CompanyInfoValues) => {
    if (autoCompleteSelected) {
      findCompanyMatches.mutate(values);
      return values;
    }

    const addressToVerify: GeocodingAddress = {
      address1: values.addressLine1,
      address2: values.addressLine2,
      city: values.city,
      state: values.state,
      countryCode: values.countryCode,
      zipPostalCode: values.zipPostalCode,
    };

    const geoResult = await parseGeoAddress(addressToVerify);

    if (geoResult.isVerified) {
      findCompanyMatches.mutate(values);
    } else {
      setPendingFormValues(values);
      setSuggestedAddress(geoResult.suggestedAddress);
      setShowVerificationModal(true);
    }

    return values;
  };

  const handleAddressSelected = (selectedAddress: GeocodingAddress) => {
    if (pendingFormValues) {
      const updatedValues: CompanyInfoValues = {
        ...pendingFormValues,
        addressLine1: selectedAddress.address1,
        addressLine2: selectedAddress.address2 || '',
        city: selectedAddress.city,
        state: selectedAddress.state,
        countryCode: selectedAddress.countryCode,
        zipPostalCode: selectedAddress.zipPostalCode,
      };

      setShowVerificationModal(false);
      findCompanyMatches.mutate(updatedValues);
    }
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setPendingFormValues(null);
    setSuggestedAddress(null);
  };

  const validate = (values: CompanyInfoValues) => {
    const requiredField = t('common.forms.requiredField');
    const errors: FormikErrors<CompanyInfoValues> = {};

    const requiredTextFields: (keyof CompanyInfoValues)[] = [
      'name',
      'typeOfBusiness',
      'countryCode',
      'addressLine1',
      'city',
      'zipPostalCode',
    ];

    requiredTextFields.forEach(field => {
      if (!values[field]?.trim()) {
        errors[field] = requiredField;
      }
    });

    if (!values.state && countrySelectorState.isStateFieldEnabled) {
      errors.state = requiredField;
    }

    if (!values.primaryPhoneNumber) {
      errors.primaryPhoneNumber = requiredField;
    } else if (
      !checkPhoneNumber(values.primaryPhoneNumber, values.countryCode)
    ) {
      errors.primaryPhoneNumber = t('users.invalidPhoneNumber');
    }

    return errors;
  };

  const countrySelectorState = useCountryStateSelector();
  const { CENTRAL_STATION_BUSINESS_TYPE } = useFeatureFlags();

  const businessTypes = getBusinessTypes(t, CENTRAL_STATION_BUSINESS_TYPE);

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}>
        {({
          dirty,
          values,
          errors,
          isValid,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          touched,
        }) => (
          <FormContainer data-test-company-info-form noValidate>
            <Text
              textAlign='right'
              data-test-required-field
              fontSize='medium'
              color='#727272'>
              {t('common.requiredFieldsShort')}
            </Text>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-company-name
              className='common-input'>
              <InputField
                name='name'
                label={t('signUp.companyInfo.companyName')}
                required
                version='v1'
              />
            </Box>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-company-type-of-business
              className='common-input'>
              <SelectField
                style={{ borderColor: '#CCCCCC', height: '50px' }}
                label={t('signUp.companyInfo.typeOfBusiness')}
                name='typeOfBusiness'
                required
                onChange={e => {
                  setFieldValue('typeOfBusiness', e.target.value);
                }}>
                <option value=''>{t('common.forms.select')}</option>
                {[...businessTypes.entries()].map(option => (
                  <option key={option[0]} value={option[0]}>
                    {option[1]}
                  </option>
                ))}
              </SelectField>
            </Box>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-address-line1
              className='common-input'>
              <LoadScript
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                libraries={libraries}>
                <Suspense fallback={<Spinner />}>
                  <AutocompleteComponent
                    initialValue={values.addressLine1}
                    setCountry={val => {
                      countrySelectorState.setCountry(val);
                      setFieldValue('countryCode', val);
                    }}
                    setStateValue={val => {
                      countrySelectorState.setState(val);
                      setFieldValue('state', val);
                    }}
                    fieldName='addressLine1'
                    setFieldValue={(field, value) => {
                      const fieldMap: Record<string, string> = {
                        address1: 'addressLine1',
                        addressLine1: 'addressLine1',
                        address2: 'addressLine2',
                        city: 'city',
                        state: 'state',
                        countryCode: 'countryCode',
                        zip: 'zipPostalCode',
                      };
                      const formField = fieldMap[field] || field;
                      setFieldValue(formField, value);
                    }}
                    setFieldTouched={setFieldTouched}
                    touched={touched}
                    errors={errors}
                    setAutoCompleteSelected={setAutoCompleteSelected}
                  />
                </Suspense>
              </LoadScript>
            </Box>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-address-line2
              className='common-input'>
              <InputField
                name='addressLine2'
                label={t('common.forms.address2')}
                version='v1'
              />
            </Box>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-city
              className='common-input'>
              <InputField
                name='city'
                label={t('common.forms.city')}
                required
                version='v1'
              />
            </Box>
            <Flex>
              <Box
                data-test-state
                marginBottom='medium'
                className='common-input'
                width='50%'>
                <StateSelectorWrapper
                  selectorState={countrySelectorState}
                  blankOption={t('common.forms.select')}
                  initialValue={values.state}
                  onStateChange={val => {
                    setFieldValue('state', val);
                  }}
                  required
                  name='state'
                  label={t('common.forms.state')}
                  version='v1'
                  error={touched.state && errors.state}
                  onBlur={() => {
                    setFieldTouched('state');
                  }}
                />
              </Box>
              <Box
                flex={1}
                marginBottom='medium'
                className='common-input'
                marginLeft='small'
                width='50%'
                data-test-postal-code>
                <InputField
                  name='zipPostalCode'
                  label={t('common.forms.zip')}
                  height='49px'
                  required
                  version='v1'
                />
              </Box>
            </Flex>
            <Box
              flex={1}
              marginBottom='medium'
              data-test-country
              className='common-input'>
              <CountrySelectorWrapper
                selectorState={countrySelectorState}
                onCountryChange={val => {
                  setFieldValue('countryCode', val);
                }}
                onBlur={() => {
                  setFieldTouched('countryCode');
                }}
                name='countryCode'
                version='v1'
                label={t('common.countries.label')}
                value={values.countryCode}
                blankOption={t('common.forms.select')}
                initialValue={values.countryCode}
                required
                error={touched.countryCode && errors.countryCode}
              />
            </Box>
            <Box
              flex={1}
              marginBottom='small'
              data-test-phone
              className='common-input'>
              <StyledPhoneInput
                className={
                  touched.primaryPhoneNumber && errors.primaryPhoneNumber
                    ? 'common-input-phone-error'
                    : 'common-input-phone'
                }
                version='v1'
                label={t('signUp.companyInfo.companyPhone')}
                name='primaryPhoneNumber'
                onChange={val => {
                  setFieldValue('primaryPhoneNumber', val);
                }}
                required
                phoneInputConfig={{
                  preferredCountries: ['us', 'ca'],
                  country: values.countryCode
                    ? values.countryCode.toLowerCase()
                    : 'us',
                  countryCodeEditable: false,
                }}
              />
            </Box>
            <Box>
              <Flex padding='large' justifyContent='center'>
                <NextButton
                  className='common-button primary'
                  variant='primary'
                  type='submit'
                  disabled={
                    !dirty ||
                    !isValid ||
                    isSubmitting ||
                    findCompanyMatches.isPending
                  }
                  data-test-submit-button>
                  {findCompanyMatches.isPending && (
                    <Spinner
                      aria-label={t('common.saving')}
                      marginX='small'
                      color='white'
                      verticalAlign='text-top'
                    />
                  )}
                  {!findCompanyMatches.isPending && t('common.next')}
                </NextButton>
              </Flex>
            </Box>
          </FormContainer>
        )}
      </Formik>
      {pendingFormValues && (
        <AddressVerificationModal
          isOpen={showVerificationModal}
          originalAddress={{
            address1: pendingFormValues.addressLine1,
            address2: pendingFormValues.addressLine2,
            city: pendingFormValues.city,
            state: pendingFormValues.state,
            countryCode: pendingFormValues.countryCode,
            zipPostalCode: pendingFormValues.zipPostalCode,
          }}
          suggestedAddress={suggestedAddress}
          onClose={handleCloseVerificationModal}
          onSelectAddress={handleAddressSelected}
        />
      )}
    </>
  );
};

export default CompanyInfoForm;
